import {
  SparklesIcon,
  ArrowUpIcon,
  ArrowUpRightIcon,
  PencilIcon,
  DocumentTextIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { StreamingHtmlViewer } from './StreamingHtmlViewer';
import HtmlViewer from '@teetsh/app/src/components/HtmlViewer';
import Input from 'components/Form/Input2';
import { trackEvent } from '@teetsh/app/src/queries/user/track';
import { useAIGenerationContext } from '@teetsh/app/src/components/Editor/AIAssist/contexts/AIGenerationContext';
import { Menu } from '@teetsh/app/src/components/Menu';
import 'twin.macro';

export interface AIGeneratedDisplayProps {
  data?: string;
  title?: string | React.ReactNode;
  onApply: () => void;
  onIgnore: () => void;
  showTypingEffect?: boolean;
  additionalActions?: ReactElement;
  // Prompt mode props
  promptMode?: boolean;
  onSubmitPrompt?: (prompt: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  promptDescription?: React.ReactNode;
  showResourcesButton?: boolean;
  onApplyAsPdf?: () => void;
  isPdfLoading?: boolean;
}

export function AIGeneratedDisplay({
  data,
  onApply,
  onIgnore,
  showTypingEffect = false,
  additionalActions,
  title,
  promptMode = false,
  onSubmitPrompt,
  isLoading = false,
  placeholder,
  promptDescription,
  showResourcesButton = false,
  onApplyAsPdf,
  isPdfLoading = false,
}: AIGeneratedDisplayProps) {
  const { t } = useTranslation(['common']);
  const [prompt, setPrompt] = useState('');

  let trackingContext: string | undefined;
  try {
    const context = useAIGenerationContext();
    trackingContext = context.trackingContext;
  } catch {
    // Context not available, tracking will be skipped
  }

  const displayTitle = title || t('common:buttons.suggestionIA');
  const hasGeneratedContent = !!data;

  function handleSubmit() {
    if (prompt.trim() && onSubmitPrompt) {
      onSubmitPrompt(prompt.trim());
      setPrompt('');
    }
  }

  function handleApply() {
    if (trackingContext) {
      trackEvent(`${trackingContext}:AIAssist:insert`, {
        action: 'custom',
      });
    }
    onApply();
    setPrompt('');
  }

  function handleIgnore() {
    if (trackingContext) {
      trackEvent(`${trackingContext}:AIAssist:ignore`, {
        action: 'custom',
      });
    }
    onIgnore();
    setPrompt('');
  }

  function handleApplyAsPdf() {
    if (trackingContext) {
      trackEvent(`${trackingContext}:AIPrompt:insertAsPDF`, {
        action: 'custom',
      });
    }
    onApplyAsPdf?.();
  }

  return (
    <div
      data-ai-toolbar
      tw="border border-neutral-quiet rounded-lg bg-white mt-2"
    >
      <div tw="bg-neutral-quiet py-2 px-4 flex items-center justify-between rounded-t-lg">
        <h5 tw="flex items-center text-base font-bold text-neutral-intense">
          <SparklesIcon tw="h-5 w-5 mr-2" />
          {displayTitle}
        </h5>
        {showResourcesButton && (
          <Button
            small
            outlined
            tw="hidden"
            icon={ArrowUpRightIcon}
            onClick={() => {
              // TODO: Open resources
            }}
          >
            {t('common:ai.resources')}
          </Button>
        )}
      </div>

      {/* Content - Show prompt description or generated result */}
      {promptMode && !hasGeneratedContent ? (
        <div tw="p-4">{promptDescription}</div>
      ) : (
        <div tw="p-4 text-sm">
          {showTypingEffect && isLoading ? (
            <StreamingHtmlViewer
              text={data || ''}
              isStreaming={true}
              onChange={() => {}}
            />
          ) : (
            <HtmlViewer text={data || ''} onChange={() => {}} />
          )}
        </div>
      )}

      {/* Footer */}
      {(!isLoading || promptMode) && (
        <div tw="px-4 py-2 border-t border-neutral-quiet flex items-center justify-end gap-2">
          {promptMode && (
            <div tw="flex-1 flex items-center flex-grow gap-2">
              <div tw="flex-grow">
                <Input
                  fill
                  value={prompt}
                  autoFocus
                  data-testid="prompt-input"
                  onChange={v => setPrompt(v.target.value)}
                  placeholder={
                    placeholder || t('common:ai.promptPlaceholders.default')
                  }
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />
              </div>
              <Button
                outlined
                icon={ArrowUpIcon}
                onClick={handleSubmit}
                loading={isLoading}
                disabled={!prompt.trim() || isLoading || isPdfLoading}
                tw="shrink-0"
                aria-label={t('common:ai.send')}
              />
            </div>
          )}
          <div tw="flex items-center gap-2">
            <Button
              onClick={handleIgnore}
              disabled={isLoading || isPdfLoading}
              minimal
            >
              {t('common:buttons.ignore')}
            </Button>
            {additionalActions}
            {promptMode && hasGeneratedContent && onApplyAsPdf ? (
              <Menu portal placement="bottom-end">
                <Menu.Button
                  intent="primary"
                  disabled={isLoading}
                  loading={isPdfLoading}
                  rightIcon={ChevronDownIcon}
                >
                  {t('common:buttons.insert')}
                </Menu.Button>
                <Menu.Items size="auto">
                  <Menu.Item
                    icon={PencilIcon}
                    text={t('common:ai.insertInActivity')}
                    onClick={handleApply}
                  />
                  <Menu.Item
                    icon={DocumentTextIcon}
                    text={t('common:ai.insertAsPdfAttachment')}
                    onClick={handleApplyAsPdf}
                  />
                </Menu.Items>
              </Menu>
            ) : (
              <Button
                onClick={
                  promptMode && !hasGeneratedContent
                    ? handleSubmit
                    : handleApply
                }
                intent="primary"
                loading={isPdfLoading}
                disabled={
                  promptMode && !hasGeneratedContent
                    ? !hasGeneratedContent || isLoading
                    : isLoading
                }
              >
                {t('common:buttons.insert')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
